import React, { useState, useEffect, useRef } from 'react';
import { Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const PatientFaqsPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Patient FAQs';
  }, []);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  
  const categoryDropdownRef = useRef(null);

  // Mock FAQ data
  const faqData = [
    {
      id: 1,
      question: 'How do I change my password?',
      answer: 'You can change your password by going to your account settings and selecting the password change option.',
      category: 'Account'
    },
    {
      id: 2,
      question: 'How do I change my credit card details?',
      answer: 'To update your credit card information, navigate to the Accounts section and select Payment Methods.',
      category: 'Payment'
    },
    {
      id: 3,
      question: 'How do I make a booking?',
      answer: 'You can make a booking by visiting the Bookings section and selecting your preferred appointment time.',
      category: 'Booking'
    },
    {
      id: 4,
      question: 'What are your operating hours?',
      answer: 'Our telehealth services are available 24/7, while in-person appointments are available during business hours.',
      category: 'General'
    },
    {
      id: 5,
      question: 'How do I cancel an appointment?',
      answer: 'You can cancel appointments through the Bookings section or by contacting our support team.',
      category: 'Booking'
    },
    {
      id: 6,
      question: 'What insurance do you accept?',
      answer: 'We accept most major insurance providers. Please check with your insurance company for coverage details.',
      category: 'Insurance'
    },
    {
      id: 7,
      question: 'How do I access my medical records?',
      answer: 'Your medical records are available in the My Details section of your patient portal.',
      category: 'Records'
    },
    {
      id: 8,
      question: 'Can I schedule a video consultation?',
      answer: 'Yes, video consultations are available. You can schedule them through the Bookings section.',
      category: 'Telehealth'
    },
    {
      id: 9,
      question: 'What should I do in case of emergency?',
      answer: 'In case of emergency, please call emergency services immediately or visit the nearest emergency room.',
      category: 'Emergency'
    },
    {
      id: 10,
      question: 'How do I update my contact information?',
      answer: 'You can update your contact information in the My Details section of your account.',
      category: 'Account'
    }
  ];

  const handleViewAll = () => {
    // In a real app, this would navigate to a full FAQ page
    console.log('Viewing all FAQs');
  };

  const handleAddCategory = () => {
    setShowCategoryDropdown(!showCategoryDropdown);
  };

  const handleCategorySelect = (category) => {
    if (!selectedCategories.includes(category)) {
      setSelectedCategories([...selectedCategories, category]);
    }
    setShowCategoryDropdown(false);
  };

  const handleCategoryRemove = (categoryToRemove) => {
    setSelectedCategories(selectedCategories.filter(cat => cat !== categoryToRemove));
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-2 px-6 pb-6">
        {/* First Block - Filter Section */}
        <div className="bg-white border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex gap-6">
            {/* Filter Category - 30% */}
            <div className="w-[30%]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter Category:
              </label>
              <div className="relative" ref={categoryDropdownRef}>
                <div className="flex items-center border border-gray-300 rounded-md bg-white">
                  <div className="flex-1 px-3 py-2 min-h-[40px] flex flex-wrap gap-1 items-center">
                    {selectedCategories.length === 0 ? (
                      <span className="text-gray-400 text-sm">Please Select</span>
                    ) : (
                      selectedCategories.map((category) => (
                        <span
                          key={category}
                          className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                        >
                          {category}
                          <button
                            onClick={() => handleCategoryRemove(category)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <button
                    onClick={handleAddCategory}
                    className="p-2 text-blue-600 hover:text-blue-800 transition-colors border-l border-gray-300"
                    title="Add Category"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Category Dropdown */}
                {showCategoryDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {['Account', 'Payment', 'Booking', 'General', 'Insurance', 'Records', 'Telehealth', 'Emergency'].map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategorySelect(category)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                        disabled={selectedCategories.includes(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* I want to - 50% */}
            <div className="w-[50%]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                I want to:
              </label>
              <input
                type="text"
                placeholder="I want to..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Second Block - Patient FAQs Section */}
        <div className="bg-white border border-blue-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-blue-600 mb-2">Patient FAQs</h2>
          <div className="border-b-2 border-blue-600 mb-4"></div>
          <div className="space-y-4">
            {faqData.slice(0, 3).map((faq) => (
              <div key={faq.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                <h3 className="text-gray-800 font-medium mb-2">{faq.question}</h3>
                <p className="text-gray-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={handleViewAll}
              className="text-blue-600 hover:text-blue-800 font-medium text-sm border border-blue-600 px-4 py-2 rounded hover:bg-blue-50 transition-colors"
            >
              View All
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-800 h-16"></div>
    </div>
  );
};

export default PatientFaqsPage; 